import { derived, get, writable } from 'svelte/store';
import { browser, dev } from '$app/environment';

import { shopify } from '$lib/shopify';

import type {
  CustomerAccessTokenCreateMutation,
  GetCustomerQuery
} from '$gql/storefront/operations';
import { region } from '$lib/region';
import type { CartBuyerIdentityInput } from '$gql/storefront/schemas';
import { geolocation } from './geolocation';
import { klavyioIdentify } from '$lib/analytics';
import { cartId } from '$lib/cart/cart-id';
import { asyncDerived } from '@square/svelte-store';
import { optimalUSCountry, parseCountryAsCode } from '$lib/country';

// only used in get-checkout-url/+server.ts but for some reason codegen is not picking up if I place the query there
/* GraphQL */ `
  query getCustomerForCheckout($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      email
    }
  }
`;

/* GraphQL */ `
  query getCustomer($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      id
      email
      firstName
      lastName
      displayName
      defaultAddress {
        id
        countryCodeV2
        city
        province
      }
      wishlist: metafield(key: "wishlist", namespace: "wishlist") {
        id
        value
      }
      tags
    }
  }
`;

/* GraphQL */ `
  mutation customerAccessTokenDelete($accessToken: String!) {
    customerAccessTokenDelete(customerAccessToken: $accessToken) {
      deletedAccessToken
    }
  }
`;

/* GraphQL */ `
  mutation updateCartBuyerIdentity($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!) {
    cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
      cart {
        id
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const SHOPIFY_US_ACCESS_TOKEN = '__hey-harper-us-access-token__';
const SHOPIFY_UK_ACCESS_TOKEN = '__hey-harper-uk-access-token__';
const SHOPIFY_EU_ACCESS_TOKEN = '__hey-harper-eu-access-token__';

export const SHOPIFY_ACCESS_TOKEN = {
  us: SHOPIFY_US_ACCESS_TOKEN,
  uk: SHOPIFY_UK_ACCESS_TOKEN,
  eu: SHOPIFY_EU_ACCESS_TOKEN
} as const;

const shopifyAccessToken = derived(region, ($region) => SHOPIFY_ACCESS_TOKEN[$region]);

const getCookie = () => {
  const match = document.cookie.match(new RegExp('(^| )' + get(shopifyAccessToken) + '=([^;]+)'));

  return match?.[2];
};

const setCookie = (value: string, expires: Date) => {
  document.cookie = `${get(
    shopifyAccessToken
  )}=${value}; expires=${expires}; path=/; SameSite=strict; secure=${!dev}`;
};

const deleteCookie = () => {
  document.cookie = `${get(
    shopifyAccessToken
  )}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; SameSite=strict; secure=${!dev}`;
};

export const user = (() => {
  const { set, subscribe, update } = writable<GetCustomerQuery['customer']>(undefined);

  const login = (
    token: NonNullable<
      NonNullable<
        CustomerAccessTokenCreateMutation['customerAccessTokenCreate']
      >['customerAccessToken']
    >
  ) => {
    if (browser) {
      setCookie(token.accessToken, new Date(token.expiresAt));
      refresh({ shouldUpdateCart: true });
    }
  };

  const updateCartIdentity = async () => {
    const id = get(cartId);

    if (!id) return;

    const buyerIdentity = await identifyUserForCart();

    return get(shopify).updateCartBuyerIdentity({
      cartId: id,
      buyerIdentity
    });
  };

  const refresh = async ({ shouldUpdateCart }: { shouldUpdateCart?: boolean } = {}) => {
    const accessToken = getCookie();
    let tmp: GetCustomerQuery['customer'];

    // Try to get customer data if there's cookie
    if (accessToken) {
      tmp = (await get(shopify).getCustomer({ accessToken })).customer;
    } else {
      return user.set(null);
    }

    // Set customer data if returned, else delete token since it's invalid
    if (tmp) {
      user.set(tmp);

      if (tmp.email) {
        // Start klavyio session
        klavyioIdentify({
          $email: tmp.email,
          $first_name: tmp.firstName,
          $last_name: tmp.lastName
        });
      }

      // update cart here
      if (shouldUpdateCart) {
        updateCartIdentity();
      }
    } else {
      user.set(null);
      deleteCookie();
    }
  };

  const logout = () => {
    const accessToken = getCookie();
    if (accessToken) get(shopify).customerAccessTokenDelete({ accessToken });
    deleteCookie();
    user.set(null);
  };

  return {
    set,
    update,
    subscribe,
    login,
    refresh,
    logout,
    getToken: getCookie,
    updateCartIdentity
  };
})();

const userData = asyncDerived(
  [geolocation, user, optimalUSCountry],
  async ([$geolocation, $user, $optimalUSCountry]) => {
    return {
      geolocation: $geolocation,
      user: $user,
      optimalUSCountry: $optimalUSCountry
    };
  }
);

export const identifyUserForCart = async (): Promise<CartBuyerIdentityInput> => {
  const userLocation = await userData.load();

  return {
    customerAccessToken: user.getToken() ?? undefined,
    countryCode:
      parseCountryAsCode(userLocation.optimalUSCountry) ??
      userLocation.user?.defaultAddress?.countryCodeV2 ??
      userLocation.geolocation?.country ??
      undefined
  };
};
